import React, { useState, useEffect, useMemo } from "react";
import "../styles/Booking.css";
import { SERVICES } from "../data";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import useRealtimeBookings from "../hooks/useRealtimeBookings";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";

const ADMIN_EMAIL = "hariharanmadhav@gmail.com";

const safe = (v) => (typeof v === "string" ? v : "");

const formatBookingId = (id) => {
  if (!id) return "—";
  return `BCC-${id.slice(0, 6).toUpperCase()}`;
};

function UserBookings() {
  const { user } = useAuth();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const userId = user?.uid ?? null;

  const { bookings, loading, error } = useRealtimeBookings();

  const [copiedId, setCopiedId] = useState(null);

  if (!user) return <p className="muted">▶ Login to see bookings</p>;
  if (loading) return <p className="muted">⏳ Loading bookings...</p>;
  if (error) return <p className="error">⚠️ {error.message}</p>;
  if (!bookings?.length) return <p className="muted">📭 No bookings yet</p>;

  const visibleBookings = isAdmin
    ? bookings
    : bookings.filter((b) => b.userId === userId);

  return (
    <motion.div
      className="booking-list"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {visibleBookings.map((b) => (
        <motion.div
          key={b.id}
          className="booking-card"
          whileHover={{ scale: 1.02 }}
        >
          <div className="booking-id-row">
            <span className="booking-id-label">Booking ID</span>
            <span className="booking-id-value">
              {formatBookingId(b.id)}
            </span>
            <button
              className="copy-btn"
              onClick={() => {
                navigator.clipboard.writeText(b.id);
                setCopiedId(b.id);
                toast.success("Booking ID copied 📋");
                setTimeout(() => setCopiedId(null), 1500);
              }}
            >
              {copiedId === b.id ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="booking-card-header">
            <h5 className="booking-card-title">
              {safe(b.service)}
            </h5>
            <span className={`booking-status ${b.status || "pending"}`}>
              {b.status || "pending"}
            </span>
          </div>

          <div className="booking-card-body">
            <p>
              📅 <strong>{b.date}</strong> at ⏰ <strong>{b.time}</strong>
            </p>
            <p>📝 {b.notes || "No notes provided"}</p>
            {isAdmin && (
              <p className="muted">
                👤 User ID: {b.userId?.slice(0, 10)}…
              </p>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

export default function Booking() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const querySlug = useMemo(
    () => new URLSearchParams(location.search).get("service"),
    [location.search]
  );

  const prefSlugFromStorage = localStorage.getItem("prefServiceSlug");

  const resolvedSlug = querySlug || prefSlugFromStorage || "";
  const resolvedTitle =
    SERVICES.find((s) => s.slug === resolvedSlug)?.title || "";

  const [form, setForm] = useState({
    service: resolvedTitle,
    date: "",
    time: "",
    name: "",
    phone: "",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [lastBookingId, setLastBookingId] = useState(null);

  useEffect(() => {
    if (!user) {
      toast.warn("Login required to continue booking", {
        position: "top-center",
      });
      nav(`/login?redirect=/book?service=${resolvedSlug}`);
      return;
    }

    setForm((f) => ({ ...f, name: user.email || f.name }));
    localStorage.removeItem("prefServiceTitle");
    localStorage.removeItem("prefServiceSlug");
  }, [user, nav, resolvedSlug]);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const validateForm = () => {
    if (!form.service || !form.date || !form.time) {
      toast.error("Please fill all required fields");
      return false;
    }

    if (!/^\d{10}$/.test(form.phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const docRef = await addDoc(collection(db, "bookings"), {
        ...form,
        userId: user.uid,
        userEmail: user.email,
        createdAt: serverTimestamp(),
        status: "pending",
      });

      setLastBookingId(docRef.id);

      try {
        await fetch("/api/notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bookingId: docRef.id,
            customerName: form.name,
            service: form.service,
            date: form.date,
            time: form.time,
            phone: form.phone,
            notes: form.notes,
            userEmail: user.email,
          }),
        });
      } catch (notifyErr) {
        console.warn("Notification failed:", notifyErr);
      }

      toast.success("Booking submitted successfully", {
        position: "top-center",
      });

      setForm({
        service: "",
        date: "",
        time: "",
        name: "",
        phone: "",
        notes: "",
      });

      setTimeout(() => nav("/"), 1200);
    } catch (err) {
      console.error("Booking error:", err);
      toast.error("Booking failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="booking-page container page">
      <div className="booking-header">
        <h2 className="booking-title gradient-text">
          🚗 Book Your Service
        </h2>
        <p className="booking-subtitle">
          Select a service, choose a date & time, and relax.
        </p>
      </div>

      {lastBookingId && (
        <div className="booking-success-id">
          <strong>Your Booking ID:</strong>{" "}
          <span>{formatBookingId(lastBookingId)}</span>
        </div>
      )}

      <motion.div
        className="booking-grid"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="booking-form">
          <h4 className="form-title">Booking Form</h4>

          <form onSubmit={handleSubmit} className="form-body">
            <div className="form-group">
              <label>Select Service</label>
              <select
                name="service"
                value={form.service}
                onChange={onChange}
                required
              >
                <option value="">Choose a service</option>
                {SERVICES.map((s) => (
                  <option key={s.slug} value={s.title}>
                    {s.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row two-col">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={onChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Time</label>
                <input
                  type="time"
                  name="time"
                  value={form.time}
                  onChange={onChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Your Name / Email</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                name="phone"
                placeholder="10-digit phone"
                value={form.phone}
                onChange={onChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                placeholder="Any request?"
                value={form.notes}
                onChange={onChange}
              />
            </div>

            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Booking"}
            </button>
          </form>
        </div>

        <div className="booking-sidebar">
          <div className="booking-help">
            <h4>Need Help?</h4>
            <p className="muted">
              <strong>📞 9626727777</strong>
            </p>
            <p className="muted">
              <strong>Mon–Sat, 9 AM–6 PM</strong>
            </p>
          </div>

          <motion.div
            className="booking-history"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h4>Your Previous Bookings</h4>
            <UserBookings />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
